import apiClient from './apiClient';

export interface EnvNamespaces {
    prod: string;
    dev: string;
    demo: string;
}

export interface AzureAdIdpEnv {
    tenantId: string;
    clientId: string;
    clientSecret: string;
}

export interface ProvisioningDefaults {
    keycloakAdminUrl: string;
    jwksBaseUrl: string;
    jwksHost: string;
    jwksPort: number;
    // Tenant-provisioning wizard defaults — override server/config/index.ts `tenantProvisioning`
    // when set. Azure AD client secrets are Key-Vault-only and never appear here.
    rootDomainApp?: string;
    rootDomainLocal?: string;
    namespaces?: EnvNamespaces;
    // Per-environment override of the derived TenantsSettings.EnvironmentTag. Blank = derive
    // from the resolved namespace (server/services/tenantProvisioningDefaults.ts).
    environmentTags?: EnvNamespaces;
    jwksLocalHost?: string;
    jwksLocalPort?: number;
    secretStoreNonLocal?: string;
    secretStoreLocalAzure?: string;
    secretStoreLocalConsul?: string;
    localConsulAddr?: string;
    localVaultAddr?: string;
    azureAdIdp?: {
        prod: AzureAdIdpEnv;
        dev: AzureAdIdpEnv;
        demo: AzureAdIdpEnv;
    };
}

export const getProvisioningDefaults = async (): Promise<ProvisioningDefaults> => {
    const res = await apiClient.get('/provisioning-defaults');
    return res.data.data;
};

export const updateProvisioningDefaults = async (data: Partial<ProvisioningDefaults>): Promise<ProvisioningDefaults> => {
    const res = await apiClient.put('/provisioning-defaults', data);
    return res.data.data;
};

// ── Environment tag picker ───────────────────────────────────────────────────
// The environment tag is never free text: the resolution rule (override -> config -> derived
// from the namespace) lives server-side only, in server/services/tenantProvisioningDefaults.ts.
// This endpoint returns its already-resolved output so the UI renders a constrained picker
// without re-implementing the rule.
export type EnvironmentTagSource = 'override' | 'config' | 'derived' | 'none';

export interface EnvironmentTagOptions {
    environment: 'prod' | 'dev' | 'demo';
    /** Resolved in-cluster namespace. '' when none is configured. */
    namespace: string;
    /** Tags that namespace actually loads. [] = unknown namespace — any tag is dangerous. */
    options: string[];
    /** Tag in force today, honouring the saved override. */
    resolved: string;
    source: EnvironmentTagSource;
    /** `resolved` is set but not one of `options` — tenants would be invisible to every runtime. */
    unmatched: boolean;
    /** What a blank override ("derive automatically") produces. */
    derived: { tag: string; source: EnvironmentTagSource };
}

export type EnvironmentTagOptionsMap = Record<'prod' | 'dev' | 'demo', EnvironmentTagOptions>;

export const getEnvironmentTagOptions = async (): Promise<EnvironmentTagOptionsMap> => {
    const res = await apiClient.get('/provisioning-defaults/environment-tags');
    return res.data.data.environments;
};
